import {
    Address,
    Consignment,
    Country,
    CustomerAddress,
    FormField,
} from '@bigcommerce/checkout-sdk'
import React, { Component, ReactNode } from 'react'
import { connect } from 'react-redux'
import B3Checkout from '../b3checkout'

import { isValidCustomerAddress, AddressForm, AddressSelect } from '../address'
import { connectFormik, ConnectFormikProps } from '../common/form'
import { Fieldset } from '../ui/form'
import { LoadingOverlay } from '../ui/loading'

import { SingleShippingFormValues } from './SingleShippingForm'

export interface ShippingAddressFormProps {
    addresses: CustomerAddress[]
    address?: Address
    consignments: Consignment[]
    countries?: Country[]
    countriesWithAutocomplete: string[]
    googleMapsApiKey?: string
    isLoading: boolean
    formFields: FormField[]
    shouldShowSaveAddress?: boolean
    onUseNewAddress(): void
    onFieldChange(fieldName: string, value: string): void
    onAddressSelect(address: Address): void
}

interface B3Props {
    isB3User: boolean
    checkoutSettings: any
}

const addressFieldName = 'shippingAddress'

class ShippingAddressForm extends Component<
    B3Props &
    ShippingAddressFormProps &
    ConnectFormikProps<SingleShippingFormValues>
> {
    validateAddress() {
        const {
            address: shippingAddress,
            addresses,
            formFields,
            isB3User,
        } = this.props

        if (!isB3User) return isValidCustomerAddress(shippingAddress, addresses, formFields)

        const isValidateExtrasField = B3Checkout.extrasFiles.isValidExtrasFiles('shipping')
        return isValidateExtrasField && B3Checkout.utils.util.validateAddress(shippingAddress, addresses, formFields)
    }

    get shouldShowEnterNewAddress() {
        const { checkoutSettings } = this.props

        const hasValidCustomerAddress = this.validateAddress()
        return hasValidCustomerAddress && checkoutSettings['shipping.address.add']
    }

    render(): ReactNode {
        const {
            addresses,
            address: shippingAddress,
            onAddressSelect,
            onUseNewAddress,
            shouldShowSaveAddress,
            countries,
            countriesWithAutocomplete,
            formFields,
            isLoading,
            googleMapsApiKey,
            formik: {
                values: { shippingAddress: formAddress },
                setFieldValue,
            },
        } = this.props

        const hasAddresses = addresses && addresses.length > 0
        const hasValidCustomerAddress = this.validateAddress()

        return (
            <Fieldset id='checkoutShippingAddress'>
                {hasAddresses && (
                    <Fieldset id='shippingAddresses'>
                        <LoadingOverlay isLoading={isLoading}>
                            <AddressSelect
                                addresses={addresses}
                                onSelectAddress={onAddressSelect}
                                onUseNewAddress={onUseNewAddress}
                                selectedAddress={
                                    hasValidCustomerAddress ? shippingAddress : undefined
                                }
                            />
                        </LoadingOverlay>
                    </Fieldset>
                )}

                {this.shouldShowEnterNewAddress && (
                    <p>
                        OR{' '}
                        <a onClick={onUseNewAddress}>
                            {B3Checkout.locales.tips['b3.addresses.enterNew']}
                        </a>
                    </p>
                )}

                {!hasValidCustomerAddress && (
                    <LoadingOverlay isLoading={isLoading} unmountContentWhenLoading>
                        <AddressForm
                            countries={countries}
                            countriesWithAutocomplete={countriesWithAutocomplete}
                            countryCode={formAddress && formAddress.countryCode}
                            fieldName={addressFieldName}
                            formFields={formFields}
                            googleMapsApiKey={googleMapsApiKey}
                            onAutocompleteToggle={this.handleAutocompleteToggle}
                            onChange={this.handleChange}
                            setFieldValue={this.setFieldValue}
                            shouldShowSaveAddress={shouldShowSaveAddress}
                            isLoadingB2bAddress={true}
                            setShippingFieldValue={setFieldValue}
                        />
                    </LoadingOverlay>
                )}
            </Fieldset>
        )
    }

    private setFieldValue: (fieldName: string, fieldValue: string) => void = (
        fieldName,
        fieldValue
    ) => {
        const {
            formik: { setFieldValue },
            formFields,
        } = this.props

        const customFormFieldNames = formFields
            .filter(field => field.custom)
            .map(field => field.name)

        const formFieldName = customFormFieldNames.includes(fieldName)
            ? `customFields.${fieldName}`
            : fieldName

        setFieldValue(`${addressFieldName}.${formFieldName}`, fieldValue)
    }

    private handleChange: (fieldName: string, value: string) => void = (
        fieldName,
        value
    ) => {
        const { onFieldChange } = this.props

        onFieldChange(fieldName, value)
    }

    private handleAutocompleteToggle: (state: {
        inputValue: string
        isOpen: boolean
    }) => void = ({ isOpen, inputValue }) => {
        const { onFieldChange } = this.props

        if (!isOpen) {
            onFieldChange('address1', inputValue)
        }
    }
}

const mapStateToProps = (state: { global: { isB3User: boolean, checkoutSettings: any } }) => {
    const {
        global: { isB3User, checkoutSettings },
    } = state

    return {
        isB3User,
        checkoutSettings,
    }
}

export default connect(mapStateToProps)(connectFormik(ShippingAddressForm))
